import { Injectable } from '@angular/core';
import { STYLE_SETTINGS } from '../../_core/state/session/session.store';
import { BehaviorSubject, Observable } from 'rxjs';

export enum Theme {
	DARK = 'DARK',
	LIGHT = 'LIGHT'
}

@Injectable({
	providedIn: 'root'
})
export class ThemeService {
	private activeTheme = Theme.LIGHT;

	private customStylesLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public readonly stylesLoaded$: Observable<boolean> = this.customStylesLoaded.asObservable();

	private getSavedTheme(theme: string | null): Theme | null {
		if (!theme) {
			return null;
		}

		return theme === Theme.DARK ? Theme.DARK : Theme.LIGHT;
	}

	public setCustomThemes(settings: any) {
		const savedTheme = this.getSavedTheme(localStorage.getItem('theme'));
		let appliedTheme = false;

		// cleanup
		if (settings && !settings.customStyles) {
			localStorage.removeItem(STYLE_SETTINGS);
			document.getElementById(STYLE_SETTINGS)?.remove();
			this.customStylesLoaded.next(true);
			return;
		}

		const customStyles = settings?.customStyles || localStorage.getItem(STYLE_SETTINGS);

		if (customStyles) {
			const style = (document.getElementById(STYLE_SETTINGS) as HTMLStyleElement | null) ?? document.createElement('style');
			style.id = STYLE_SETTINGS;
			style.textContent = customStyles;
			if (!style.parentElement) {
				document.head.appendChild(style);
			}
			localStorage.setItem(STYLE_SETTINGS, customStyles);

			if (!savedTheme && customStyles.includes('.default-theme')) {
				const snippet = this.extractAroundDefaultTheme(customStyles);
				this.setTheme(snippet.includes('.light-theme') ? Theme.LIGHT : Theme.DARK);
				appliedTheme = true;
			}
		}

		if (savedTheme) {
			this.setTheme(savedTheme);
		} else if (!appliedTheme) {
			document.body.classList.remove('default-theme', 'dark-theme', 'light-theme');
			if (settings?.canChangeTheme) {
				document.body.classList.add('light-theme');
				this.activeTheme = Theme.LIGHT;
			} else {
				document.body.classList.add('default-theme', 'dark-theme');
				this.activeTheme = Theme.DARK;
			}
		}

		this.customStylesLoaded.next(!!customStyles || !!settings);
	}

	public useBaseThemeOnly(): void {
		document.getElementById(STYLE_SETTINGS)?.remove();
		this.customStylesLoaded.next(true);
	}

	setTheme(theme: Theme): void {
		this.activeTheme = theme;
		localStorage.setItem('theme', theme);

		// Remove all theme classes first
		document.body.classList.remove('default-theme', 'dark-theme', 'light-theme');

		// Add the appropriate theme class
		if (theme === Theme.DARK) {
			document.body.classList.add('dark-theme');
		} else {
			document.body.classList.add('light-theme');
		}
	}

	initializeTheme(): void {
		const savedTheme = localStorage.getItem('theme') as Theme;

		if (savedTheme) {
			this.setTheme(savedTheme === Theme.DARK ? Theme.DARK : Theme.LIGHT);
			return;
		}

		// Default to light theme if no saved theme.
		document.body.classList.remove('default-theme', 'dark-theme', 'light-theme');
		document.body.classList.add('light-theme');
	}

	toggleTheme(): void {
		// if toggle is used, default theme is removed and it resets on page refresh
		document.body.classList.remove('default-theme');

		if (this.activeTheme == Theme.DARK) {
			document.body.classList.remove('dark-theme');
			document.body.classList.add('light-theme');
			this.activeTheme = Theme.LIGHT;
			localStorage.setItem('theme', Theme.LIGHT);
		} else {
			document.body.classList.remove('light-theme');
			document.body.classList.add('dark-theme');
			this.activeTheme = Theme.DARK;
			localStorage.setItem('theme', Theme.DARK);
		}
	}

	getTheme() {
		const theme = localStorage.getItem('theme') as Theme;
		this.activeTheme = theme ?? this.activeTheme;
		return this.activeTheme;
	}

	private extractAroundDefaultTheme(css: string, contextLength = 30): string {
		const target = '.default-theme';
		let index = css.indexOf(target);

		return css.slice(Math.max(0, index - contextLength), Math.max(0, index + target.length + contextLength));
	}
}
